/**
 * Connectors — Sprint C' minimal surface.
 *
 * Lists installed connectors per tenant and lets an operator trigger
 * a sync manually. The list comes from /v1/connectors/installed; the
 * sync button POSTs to /v1/connectors/{name}/sync, which returns 202
 * with a correlation_id (used to follow the resulting `sync.completed`
 * event in the Inbox / Activity feed).
 */

import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, ChevronDown, ChevronRight, History, Loader2, Plug,
  RefreshCw, ShieldCheck, ShieldOff, XCircle,
} from "lucide-react";
import { Badge, Button, Card, CardHeader, cn } from "@vseh/ui";

import {
  connectorsApi,
  type ConnectorHealthDto,
  type InstallationDto, type SyncAcceptedDto,
  type SyncRunDto,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../shared/components/States";
import { TelegramBotWizard } from "./connectors/TelegramBotWizard";
import { BridgeStatusCard } from "./connectors/BridgeStatusCard";


const TG_USER_COMPOSE = (installation: string, secret: string) => `  agent-tg-user-${installation.slice(0, 8)}:
    build:
      context: .
      dockerfile: core/connectors/telegram-user/Dockerfile
    environment:
      OS_CORE_URL:        https://os.vsteh.ru
      INSTALLATION_ID:    ${installation}
      BRIDGE_SECRET:      ${secret}
      TELETHON_API_ID:    \${TELETHON_API_ID}      # из my.telegram.org
      TELETHON_API_HASH:  \${TELETHON_API_HASH}
      TELETHON_PHONE:     \${TELETHON_PHONE}        # +79991234567
      MINIO_ENDPOINT:     minio:9000
      MINIO_ACCESS_KEY:   \${MINIO_ACCESS_KEY}
      MINIO_SECRET_KEY:   \${MINIO_SECRET_KEY}
    networks: [vseh-network]`;


const WA_COMPOSE = (installation: string, secret: string) => `  agent-wa-${installation.slice(0, 8)}:
    build:
      context: .
      dockerfile: core/connectors/whatsapp/Dockerfile
    environment:
      OS_CORE_URL:       https://os.vsteh.ru
      INSTALLATION_ID:   ${installation}
      BRIDGE_SECRET:     ${secret}
      MINIO_ENDPOINT:    minio:9000
      MINIO_ACCESS_KEY:  \${MINIO_ACCESS_KEY}
      MINIO_SECRET_KEY:  \${MINIO_SECRET_KEY}
    ports:
      - "8090:8080"   # QR-код доступен на /qr — отсканировать в WA → меню → Связанные устройства
    networks: [vseh-network]`;


// Which connectors expose a `POST /sync` endpoint right now. Others
// (telegram outbound, future avito) are read-only / event-driven.
const SYNC_CAPABLE = new Set(["vsteh-ru"]);


export function ConnectorsPage(): JSX.Element {
  const t = useT();
  const { data, error, loading, refetch } = useFetch<InstallationDto[]>(
    (signal) => connectorsApi.listInstalled({ signal }),
    [],
  );

  // Sprint G1 — health rollup, indexed by connector_name so the
  // per-row component does an O(1) lookup. We don't block the
  // installations list on this — a failed health fetch just
  // means the rows render without pills, which is the same
  // experience as pre-F4.
  const { data: health } = useFetch<ConnectorHealthDto[]>(
    (signal) => connectorsApi.health({ signal }),
    [],
  );
  const healthByName: Record<string, ConnectorHealthDto> = {};
  for (const h of health ?? []) healthByName[h.connector_name] = h;

  const telegramInstall      = (data ?? []).find((i) => i.connector_name === "telegram");
  const telegramUserInstall  = (data ?? []).find((i) => i.connector_name === "telegram-user");
  const whatsappInstall      = (data ?? []).find((i) => i.connector_name === "whatsapp");

  return (
    <div className="flex flex-col gap-4">
      <TelegramBotWizard
        existing={telegramInstall}
        onChange={refetch}
      />

      <BridgeStatusCard
        connectorName="telegram-user"
        channelKey="telegram-user"
        title="Telegram-аккаунт (MTProto)"
        subtitle="Слушать публичные группы и каналы как обычный пользователь — без бота"
        helpUrl="https://my.telegram.org"
        helpLabel="Получите api_id + api_hash на my.telegram.org → API development tools"
        extraFields={[
          { name: "telethon_api_id",   label: "API ID",   placeholder: "12345678" },
          { name: "telethon_api_hash", label: "API HASH", placeholder: "0123456789abcdef…", type: "password" },
          { name: "telethon_phone",    label: "Телефон",  placeholder: "+79991234567" },
        ]}
        composeSnippet={TG_USER_COMPOSE}
        existing={telegramUserInstall}
        onChange={refetch}
      />

      <BridgeStatusCard
        connectorName="whatsapp"
        channelKey="whatsapp"
        title="WhatsApp"
        subtitle="Логин через QR-код — читать все чаты вашей симки (Web Multi-Device)"
        helpUrl="https://faq.whatsapp.com/1317564962315842"
        helpLabel="Откройте WhatsApp → Меню → Связанные устройства (когда контейнер поднимется)"
        extraFields={[
          { name: "linked_phone", label: "Номер телефона (для маркировки)", placeholder: "+79991234567" },
        ]}
        composeSnippet={WA_COMPOSE}
        existing={whatsappInstall}
        onChange={refetch}
      />

      <Card>
        <CardHeader
          title={t("connectors.title")}
          description={t("connectors.subtitle")}
          actions={
            <Button variant="ghost" size="xs" onClick={refetch}>
              {t("common.refresh")}
            </Button>
          }
        />

        {loading && !data && <LoadingBlock />}
        {error && <ErrorBlock error={error} onRetry={refetch} />}

        {data && data.length === 0 && (
          <EmptyBlock
            title={t("connectors.empty.title")}
            description={t("connectors.empty.description")}
          />
        )}

        {data && data.length > 0 && (
          <ul className="space-y-2">
            {data.map((inst) => (
              <ConnectorRow
                key={inst.id}
                installation={inst}
                health={healthByName[inst.connector_name]}
                onAnyAction={refetch}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}


function ConnectorRow({
  installation, health, onAnyAction,
}: {
  installation: InstallationDto;
  /** Sprint G1 — health snapshot for THIS connector (or
   *  undefined while the rollup hasn't loaded yet). Drives
   *  the green/amber/red pill next to the connector name. */
  health:       ConnectorHealthDto | undefined;
  onAnyAction:  () => void;
}): JSX.Element {
  const t = useT();
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<SyncAcceptedDto | null>(null);
  const [err, setErr] = useState<Error | null>(null);

  const canSync = SYNC_CAPABLE.has(installation.connector_name);

  const onSync = useCallback(async () => {
    setSyncing(true);
    setErr(null);
    setResult(null);
    try {
      const r = await connectorsApi.sync(installation.connector_name, {});
      setResult(r);
      onAnyAction();
    } catch (e) {
      setErr(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setSyncing(false);
    }
  }, [installation.connector_name, onAnyAction]);

  return (
    <li className="rounded-md border border-base-200 bg-surface p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Plug className="size-4 text-base-500" aria-hidden="true" />
            <span className="font-mono text-sm text-base-900">
              {installation.connector_name}
            </span>
            <Badge tone="neutral">v{installation.connector_version}</Badge>
            {installation.enabled ? (
              <Badge tone="success" dot>
                <ShieldCheck className="mr-1 size-3" aria-hidden="true" />
                {t("connectors.badge.enabled")}
              </Badge>
            ) : (
              <Badge tone="danger" dot>
                <ShieldOff className="mr-1 size-3" aria-hidden="true" />
                {t("connectors.badge.disabled")}
              </Badge>
            )}
            <HealthPill health={health} />
          </div>
          {installation.last_health_status && (
            <div className="mt-1 text-2xs text-base-500">
              health: <span className="font-mono">{installation.last_health_status}</span>
            </div>
          )}
        </div>

        {canSync && installation.enabled && (
          <Button
            variant="ai"
            size="sm"
            leftIcon={<RefreshCw className={cn("size-4", syncing && "animate-spin")} />}
            loading={syncing}
            onClick={onSync}
          >
            {t("connectors.sync")}
          </Button>
        )}
      </div>

      {result && (
        <div className="mt-2 rounded-sm border border-success-200 bg-success-50 px-3 py-1.5 text-xs text-success-700">
          {t("connectors.sync.started")}{" "}
          <span className="font-mono">{result.correlation_id}</span>
        </div>
      )}
      {err && (
        <div className="mt-2 rounded-sm border border-danger-200 bg-danger-50 px-3 py-1.5 text-xs text-danger-700">
          {err.message}
        </div>
      )}

      <SyncHistory source={installation.connector_name} />
    </li>
  );
}


/**
 * Collapsible "история запусков" drawer per connector row.
 *
 * Lazy-fetches on first expand to keep the page render snappy; once
 * fetched we cache the result in component state, refresh-on-demand
 * via the chevron click. Auto-refresh hooks are intentionally absent —
 * sync runs are infrequent enough that polling would waste cycles.
 */
function SyncHistory({ source }: { source: string }): JSX.Element {
  const t = useT();
  const [open,    setOpen]    = useState(false);
  const [runs,    setRuns]    = useState<SyncRunDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState<Error | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const rs = await connectorsApi.syncRuns({ source, limit: 10 });
      setRuns(rs);
    } catch (e) {
      setErr(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => {
    if (open && runs === null) void load();
  }, [open, runs, load]);

  return (
    <div className="mt-3 border-t border-base-100 pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-base-500 hover:text-base-700"
      >
        {open
          ? <ChevronDown   className="size-3.5" aria-hidden="true" />
          : <ChevronRight  className="size-3.5" aria-hidden="true" />}
        <History className="size-3.5" aria-hidden="true" />
        {t("connectors.history")}
      </button>

      {open && (
        <div className="mt-2">
          {loading && (
            <div className="flex items-center gap-1.5 text-2xs text-base-500">
              <Loader2 className="size-3.5 animate-spin" />
              {t("common.loading")}
            </div>
          )}
          {err && (
            <div className="text-2xs text-danger-700">{err.message}</div>
          )}
          {runs && runs.length === 0 && (
            <div className="text-2xs text-base-400">{t("connectors.history.empty")}</div>
          )}
          {runs && runs.length > 0 && (
            <table className="w-full text-2xs">
              <thead className="text-base-500">
                <tr>
                  <th className="py-1 pr-2 text-left font-medium">{t("connectors.history.col.status")}</th>
                  <th className="py-1 pr-2 text-left font-medium">{t("connectors.history.col.started")}</th>
                  <th className="py-1 pr-2 text-right font-medium">⏱</th>
                  <th className="py-1 pr-2 text-right font-medium">+</th>
                  <th className="py-1 pr-2 text-right font-medium">~</th>
                  <th className="py-1 pr-2 text-right font-medium">−</th>
                  <th className="py-1 pr-2 text-right font-medium">err</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => <SyncRunRow key={r.id} run={r} />)}
              </tbody>
            </table>
          )}
          <div className="mt-1.5">
            <Button variant="ghost" size="xs" onClick={load} disabled={loading}>
              {t("connectors.history.refresh")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function SyncRunRow({ run }: { run: SyncRunDto }): JSX.Element {
  // Status icon doubles as a "click for correlation_id" tooltip target —
  // operators copy the correlation_id into Inbox / activity feed to
  // trace what the run did downstream.
  const statusIcon = run.status === "succeeded"
    ? <CheckCircle2 className="size-3 text-success-600" aria-hidden="true" />
    : run.status === "failed"
    ? <XCircle      className="size-3 text-danger-600"  aria-hidden="true" />
    : <Loader2      className="size-3 animate-spin text-ai-600" aria-hidden="true" />;
  return (
    <tr
      className="border-t border-base-100 align-top"
      title={run.correlation_id ? `correlation_id: ${run.correlation_id}` : ""}
    >
      <td className="py-1 pr-2">
        <span className="flex items-center gap-1">
          {statusIcon}
          {run.status}
          {run.dry_run && <Badge tone="neutral">dry</Badge>}
        </span>
        {run.error_summary && (
          <div className="text-2xs text-danger-700">{run.error_summary}</div>
        )}
      </td>
      <td className="py-1 pr-2 text-base-600">
        {new Date(run.started_at).toLocaleString("ru-RU")}
      </td>
      <td className="py-1 pr-2 text-right tabular-nums text-base-600">
        {run.duration_s != null ? `${run.duration_s.toFixed(1)}s` : "—"}
      </td>
      <td className="py-1 pr-2 text-right tabular-nums text-success-700">
        {run.created || ""}
      </td>
      <td className="py-1 pr-2 text-right tabular-nums text-base-700">
        {run.updated || ""}
      </td>
      <td className="py-1 pr-2 text-right tabular-nums text-base-500">
        {run.removed || ""}
      </td>
      <td className={cn(
        "py-1 pr-2 text-right tabular-nums",
        run.errors > 0 ? "text-danger-700" : "text-base-400",
      )}>
        {run.errors || ""}
      </td>
    </tr>
  );
}


/**
 * Sprint G1 — green/amber/red health pill next to a
 * connector's name. Reads the F4 rollup:
 *
 *   green  — recent successful run + no failure streak
 *   amber  — last success > freshness window (stale) OR
 *            <3 consecutive failures since last success
 *   red    — ≥ 3 consecutive failures, or no success ever
 *
 * Renders nothing while `health` is undefined (rollup
 * fetch in flight) — keeps the row layout stable.
 */
function HealthPill({
  health,
}: { health: ConnectorHealthDto | undefined }): JSX.Element | null {
  const t = useT();
  if (!health) return null;

  if (health.is_healthy) {
    return <Badge tone="success" dot>{t("connectors.health.healthy")}</Badge>;
  }
  if (health.stale && health.consecutive_failures === 0) {
    return <Badge tone="heritage" dot>{t("connectors.health.stale")}</Badge>;
  }
  if (health.consecutive_failures >= 3) {
    return (
      <Badge tone="danger" dot title={health.recent_errors_summary.join(" · ")}>
        {t("connectors.health.failing", { n: health.consecutive_failures })}
      </Badge>
    );
  }
  return (
    <Badge tone="heritage" dot title={health.recent_errors_summary.join(" · ")}>
      {t("connectors.health.degraded", { n: health.consecutive_failures })}
    </Badge>
  );
}
